import { EXO_SKELETON_EVENT_NAMES } from '../consts';
import {
  createPostMessageEventHandler,
  ExoSkeletonRuntimeDataEvent,
  hasExoSkeletonListenerInitialized,
  sendRuntimeDataEvent,
} from '../runtime';

export interface ImportRemoteFailedData {
  scope: string;
  error: Error;
  metadata?: Record<string, unknown>;
}

const IMPORT_REMOTE_FAILED_EVENT_NAME =
  EXO_SKELETON_EVENT_NAMES.IMPORT_REMOTE_FAILED_EVENT_NAME;

type RuntimeImportRemoteFailedEvent = ExoSkeletonRuntimeDataEvent<
  typeof IMPORT_REMOTE_FAILED_EVENT_NAME,
  ImportRemoteFailedData
>;

function isImportRemoteFailedDataEvent(
  data: unknown,
): data is RuntimeImportRemoteFailedEvent {
  return (
    (data as RuntimeImportRemoteFailedEvent)?.type ===
    IMPORT_REMOTE_FAILED_EVENT_NAME
  );
}

export interface CreateImportRemoteFailedListenerParams {
  onImportRemoteFailed: (event: RuntimeImportRemoteFailedEvent) => void;
}

export function createImportRemoteFailedListener({
  onImportRemoteFailed,
}: CreateImportRemoteFailedListenerParams): { dispose: () => void } {
  return createPostMessageEventHandler(
    IMPORT_REMOTE_FAILED_EVENT_NAME,
    function ({ data }: MessageEvent<unknown>) {
      if (isImportRemoteFailedDataEvent(data)) {
        onImportRemoteFailed(data);
      }
    },
  );
}

export function handleRemoteImportError(
  error: Error | string,
  scope: string,
  metadata?: Record<string, unknown>,
) {
  const errorObject = error instanceof Error ? error : new Error(String(error));

  const errorData: ImportRemoteFailedData = {
    scope,
    error: errorObject,
    metadata,
  };

  console.warn(
    `Remote import failed for scope "${scope}".`,
    `Error message: ${errorObject.message}`,
    metadata || '',
  );

  if (!hasExoSkeletonListenerInitialized(IMPORT_REMOTE_FAILED_EVENT_NAME)) {
    console.warn(
      '"handleRemoteImportError" was called, but the ExoSkeleton host did not initialize the "createImportRemoteFailedListener". Data sent:',
      errorData,
    );
  }

  return sendRuntimeDataEvent<
    typeof IMPORT_REMOTE_FAILED_EVENT_NAME,
    ImportRemoteFailedData
  >(IMPORT_REMOTE_FAILED_EVENT_NAME, errorData);
}
